<template>
  <div class="centered-container">
    <ShareLink v-if="instanceId" :instanceId="instanceId" />
    <h2 class="promo-heading">Add things, rate them, then save. Results stay live as votes come in.</h2>
    <h2 class="sub-heading">{{ instanceData.description }}</h2>

    <div class="content-box">
      <div class="box-heading">
        <h3>Open</h3>
      </div>
      <div class="box-content">
        <p v-if="!openThings.length" class="empty-note">All current things are saved. Add another below.</p>
        <ul>
          <li v-for="thing in openThings" :key="thing.id">
            <div class="list-item open-item">
              <div class="item-top">
                <span class="item-name">{{ thing.thing }}</span>
                <VoteChip :instance-data="instanceData" :thing-id="thing.id" />
              </div>
              <div class="item-controls">
                <div class="star-rating">
                  <button
                    v-for="star in 5"
                    :key="star"
                    type="button"
                    class="star-button"
                    :class="{ active: getLocalRating(thing.id) >= star, disabled: isLocalVeto(thing.id) }"
                    :disabled="isLocalVeto(thing.id) || isPending(thing.id)"
                    @click="setRating(thing.id, star)"
                  >
                    <StarIcon :size="28" :fill="getLocalRating(thing.id) >= star ? '#ffc107' : '#ddd'" />
                  </button>
                </div>
                <button
                  type="button"
                  class="veto-button"
                  :class="{ active: isLocalVeto(thing.id), disabled: !isLocalVeto(thing.id) && !canVeto }"
                  :disabled="(!isLocalVeto(thing.id) && !canVeto) || isPending(thing.id)"
                  @click="toggleVeto(thing.id)"
                >
                  {{ isLocalVeto(thing.id) ? 'Vetoed' : 'Veto' }}
                </button>
              </div>
              <div v-if="!isPending(thing.id)" class="item-actions">
                <button type="button" @click="editHandler(thing)">Edit</button>
                <button type="button" @click="deleteHandler(thing)">Delete</button>
              </div>
            </div>
          </li>
        </ul>
        <div class="input-container">
          <input
            v-model="newThing"
            type="text"
            placeholder="Enter a thing"
            @keyup.enter="addHandler"
          />
          <button type="button" @click="addHandler">Add</button>
        </div>
      </div>
    </div>

    <button
      type="button"
      class="finish-button"
      :disabled="!canSave || isSaving"
      @click="submitRatings"
    >
      {{ isSaving ? 'Saving...' : 'Save ratings' }}
    </button>
    <p class="save-hint">You won’t be able to change these later.</p>

    <div v-if="savedIds.length" class="content-box">
      <div class="box-heading">
        <h3>The Best Things</h3>
      </div>
      <div class="box-content">
        <p v-if="minVetoes > 0 && rankedOptions.length" class="veto-note">
          Every option was vetoed. These had the fewest vetoes.
        </p>
        <p v-if="!rankedOptions.length" class="empty-note">No options in play yet from your saved items.</p>
        <ul>
          <li v-for="thing in rankedOptions" :key="thing.id">
            <div class="list-item result-item">
              <div class="item-top">
                <span class="item-name">{{ thing.thing }}</span>
                <VoteChip :instance-data="instanceData" :thing-id="thing.id" />
              </div>
              <div class="average-rating">
                <div class="star-rating-display">
                  <span v-for="star in 5" :key="star" class="star-display">
                    <StarIcon :size="28" fill="#ddd" custom-class="star-background" />
                    <svg class="star-fill" viewBox="0 0 35 33" xmlns="http://www.w3.org/2000/svg">
                      <defs>
                        <clipPath :id="`star-clip-${thing.id}-${star}`">
                          <rect
                            :x="starLeft"
                            :width="fillWidth(thing.averageRating, star)"
                            height="33"
                          />
                        </clipPath>
                      </defs>
                      <polygon
                        :points="STAR_POINTS"
                        fill="#ffc107"
                        :clip-path="`url(#star-clip-${thing.id}-${star})`"
                      />
                    </svg>
                  </span>
                </div>
                <span v-if="thing.averageRating > 0" class="rating-text">{{ thing.averageRating.toFixed(2) }}</span>
                <span v-else class="rating-text no-rating">No ratings</span>
              </div>
            </div>
          </li>
        </ul>
      </div>
    </div>

    <div v-if="moreVetoed.length" class="content-box">
      <div class="box-heading">
        <h3>Vetoed Things</h3>
      </div>
      <div class="box-content">
        <ul>
          <li v-for="thing in moreVetoed" :key="thing.id">
            <div class="list-item result-item">
              <div class="item-top">
                <span class="item-name">{{ thing.thing }}</span>
                <VoteChip :instance-data="instanceData" :thing-id="thing.id" />
              </div>
              <span class="vetoed-label">{{ thing.vetoCount }} veto{{ thing.vetoCount === 1 ? '' : 's' }}</span>
            </div>
          </li>
        </ul>
      </div>
    </div>

    <ParticipantList :instanceData="instanceData" />

    <ModalDialog
      :show="showEditModal"
      type="edit"
      title="Edit Thing"
      message="Editing this will clear everyone’s votes on it."
      :initialValue="editingThing?.thing || ''"
      confirmText="Save"
      @confirm="handleEditConfirm"
      @cancel="handleEditCancel"
    />

    <ModalDialog
      :show="showDeleteModal"
      type="confirm"
      title="Delete Thing"
      :message="deleteMessage"
      confirmText="Delete"
      @confirm="handleDeleteConfirm"
      @cancel="handleDeleteCancel"
    />
  </div>
</template>

<script>
import ShareLink from "@/components/ShareLink.vue";
import VoteChip from "@/components/VoteChip.vue";
import StarIcon from "@/components/StarIcon.vue";
import ParticipantList from "@/components/ParticipantList.vue";
import ModalDialog from "@/components/Modal.vue";
import { STAR_POINTS } from "@/constants/stars";
import {
  hasSavedVeto,
  savedThingIdsForUser,
  rankSavedThings
} from "@/utils/votes";

export default {
  name: "ThingBoard",
  components: {
    ShareLink,
    VoteChip,
    StarIcon,
    ParticipantList,
    ModalDialog
  },
  props: {
    instanceData: { type: Object, required: true },
    name: { type: Object, required: true },
    instanceId: { type: String, default: null },
    addThing: { type: Function, required: true },
    editThing: { type: Function, required: true },
    removeThing: { type: Function, required: true },
    saveRatings: { type: Function, required: true }
  },
  data() {
    return {
      newThing: "",
      localRatings: {},
      localVetos: [],
      isSaving: false,
      showEditModal: false,
      showDeleteModal: false,
      editingThing: null,
      deletingThing: null,
      deleteMessage: "",
      STAR_POINTS,
      starLeft: 0.99,
      starWidth: 33.01
    };
  },
  computed: {
    things() {
      return this.instanceData.things || [];
    },
    rankings() {
      return this.instanceData.rankings || {};
    },
    savedIds() {
      return savedThingIdsForUser(this.rankings, this.name.id);
    },
    openThings() {
      const saved = new Set(this.savedIds);
      return this.things.filter((thing) => !saved.has(thing.id));
    },
    canVeto() {
      if (hasSavedVeto(this.rankings, this.name.id)) return false;
      return this.localVetos.length === 0;
    },
    canSave() {
      return this.openThings.some((thing) => {
        if (this.isPending(thing.id)) return false;
        return this.getLocalRating(thing.id) >= 1 || this.isLocalVeto(thing.id);
      });
    },
    ranked() {
      return rankSavedThings(this.things, this.rankings, this.savedIds);
    },
    rankedOptions() {
      return this.ranked.options;
    },
    moreVetoed() {
      return this.ranked.moreVetoed;
    },
    minVetoes() {
      return this.ranked.minVetoes;
    }
  },
  watch: {
    savedIds() {
      this.pruneLocalVotes();
      this.isSaving = false;
    }
  },
  methods: {
    pruneLocalVotes() {
      const saved = new Set(this.savedIds);
      const nextRatings = {};
      Object.entries(this.localRatings).forEach(([id, stars]) => {
        if (!saved.has(id)) nextRatings[id] = stars;
      });
      this.localRatings = nextRatings;
      this.localVetos = this.localVetos.filter((id) => !saved.has(id));
    },
    isPending(thingId) {
      return String(thingId).startsWith("temp-");
    },
    getLocalRating(thingId) {
      return this.localRatings[thingId] || 0;
    },
    isLocalVeto(thingId) {
      return this.localVetos.includes(thingId);
    },
    setRating(thingId, stars) {
      if (this.isPending(thingId) || this.isLocalVeto(thingId)) return;
      if (this.localRatings[thingId] === stars) {
        this.localRatings = { ...this.localRatings, [thingId]: 0 };
      } else {
        this.localRatings = { ...this.localRatings, [thingId]: stars };
      }
    },
    toggleVeto(thingId) {
      if (this.isPending(thingId)) return;
      if (this.isLocalVeto(thingId)) {
        this.localVetos = this.localVetos.filter((id) => id !== thingId);
        return;
      }
      if (!this.canVeto) return;
      this.localVetos = [thingId];
      this.localRatings = { ...this.localRatings, [thingId]: 0 };
    },
    async addHandler() {
      if (this.newThing.trim() === "") return;
      const thingText = this.newThing.trim();
      const temp = {
        id: "temp-" + Date.now(),
        thing: thingText,
        addedBy: this.name.id
      };
      // eslint-disable-next-line vue/no-mutating-props
      this.instanceData.things.push(temp);
      this.newThing = "";
      await this.addThing(thingText);
    },
    editHandler(thing) {
      this.editingThing = thing;
      this.showEditModal = true;
    },
    async handleEditConfirm(editedValue) {
      if (this.editingThing) {
        const thing = this.instanceData.things.find((item) => item.id === this.editingThing.id);
        if (thing) {
          // eslint-disable-next-line vue/no-mutating-props
          thing.thing = editedValue;
        }
        await this.editThing(this.editingThing.id, editedValue);
      }
      this.handleEditCancel();
    },
    handleEditCancel() {
      this.showEditModal = false;
      this.editingThing = null;
    },
    deleteHandler(thing) {
      const addedBy = (this.instanceData.names || []).find((person) => person.id === thing.addedBy);
      this.deletingThing = thing;
      this.deleteMessage = `Are you sure you want to remove "${thing.thing}"${addedBy ? ` added by ${addedBy.name}` : ""}?`;
      this.showDeleteModal = true;
    },
    async handleDeleteConfirm() {
      if (this.deletingThing) {
        // eslint-disable-next-line vue/no-mutating-props
        this.instanceData.things = this.instanceData.things.filter((item) => item.id !== this.deletingThing.id);
        await this.removeThing(this.deletingThing.id);
      }
      this.handleDeleteCancel();
    },
    handleDeleteCancel() {
      this.showDeleteModal = false;
      this.deletingThing = null;
      this.deleteMessage = "";
    },
    async submitRatings() {
      if (!this.canSave || this.isSaving) return;
      this.isSaving = true;
      const ratings = {};
      this.openThings.forEach((thing) => {
        const stars = this.getLocalRating(thing.id);
        if (stars >= 1) ratings[thing.id] = stars;
      });
      const vetos = this.localVetos.filter((id) => this.openThings.some((thing) => thing.id === id));
      const current = this.rankings[this.name.id] || { ratings: {}, vetos: [] };
      const nextRatings = { ...current.ratings, ...ratings };
      const nextVetos = [...(current.vetos || [])];
      vetos.forEach((id) => {
        if (!nextVetos.includes(id) && nextVetos.length === 0) {
          delete nextRatings[id];
          nextVetos.push(id);
        }
      });
      // eslint-disable-next-line vue/no-mutating-props
      this.instanceData.rankings = {
        ...this.rankings,
        [this.name.id]: { ratings: nextRatings, vetos: nextVetos }
      };
      try {
        await this.saveRatings(ratings, vetos);
      } catch (error) {
        this.isSaving = false;
        console.error("Error saving ratings:", error);
      }
    },
    fillWidth(average, star) {
      if (star <= average) return this.starWidth;
      if (star > average && star - 1 < average) {
        return (average - (star - 1)) * this.starWidth;
      }
      return 0;
    }
  }
};
</script>

<style scoped>
.empty-note,
.save-hint,
.veto-note {
  color: #666;
  font-size: 14px;
  line-height: 1.4;
}

.save-hint {
  margin-top: 8px;
  font-style: italic;
  text-align: center;
}

.veto-note {
  margin-bottom: 12px;
}

.list-item {
  border-bottom: 1px solid #ddd;
  padding: 12px 0;
}

li:last-child .list-item {
  border-bottom: none;
}

.item-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
}

.item-name {
  flex: 1;
  min-width: 0;
  word-break: break-word;
  text-align: left;
}

.item-controls,
.item-actions,
.average-rating {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 10px;
  flex-wrap: wrap;
}

.item-actions {
  justify-content: flex-end;
}

.star-rating,
.star-rating-display {
  display: flex;
  gap: 5px;
}

.star-button {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  margin: 0;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.star-button:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.veto-button {
  margin: 0;
  padding: 8px 16px;
  border: 2px solid #e74c3c;
  border-radius: 4px;
  background-color: white;
  color: #e74c3c;
  font-weight: 500;
  min-width: 80px;
}

.veto-button.active {
  background-color: #e74c3c;
  color: white;
}

.veto-button.disabled,
.veto-button:disabled {
  border-color: #ccc;
  color: #ccc;
  cursor: not-allowed;
}

.input-container {
  display: flex;
  gap: 8px;
  margin-top: 12px;
}

.input-container input {
  flex: 1;
  min-width: 0;
}

.input-container button {
  margin: 0;
  flex-shrink: 0;
}

.finish-button {
  width: 100%;
  margin: 16px 0 0;
}

.star-display {
  position: relative;
  display: inline-block;
  width: 28px;
  height: 28px;
}

.star-background,
.star-fill {
  position: absolute;
  left: 0;
  top: 0;
  width: 28px;
  height: 28px;
}

.rating-text {
  font-size: 18px;
  color: #666;
  font-weight: 500;
}

.rating-text.no-rating {
  color: #999;
  font-style: italic;
}

.vetoed-label {
  padding: 6px 12px;
  background-color: #e74c3c;
  color: white;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
}

@media (max-width: 600px) {
  .item-actions button,
  .veto-button,
  .input-container button,
  .finish-button {
    min-height: 44px;
  }

  .star-button {
    min-width: 36px;
    min-height: 36px;
  }

  .input-container {
    flex-wrap: wrap;
  }

  .input-container input,
  .input-container button {
    flex: 1 1 100%;
  }
}
</style>

